import { Calendar, Film, Sparkles, BarChart3 } from 'lucide-react';
import { LandingTemplate, type LandingData } from '../components/LandingTemplate';
import { LANDING_GUIDES } from '../lib/landingGuides';

const data: LandingData = {
  seo: {
    title: 'Cómo programar vídeos en TikTok, paso a paso · Zettapost',
    description:
      'Cómo programar vídeos en TikTok desde TikTok Studio y con Zettapost, desde el ordenador y sin el límite de pocos días. Prueba 3 días gratis.',
    canonical: 'https://zettapost.com/programar-tiktok',
  },
  hero: {
    eyebrow: 'Programar TikTok',
    headlinePre: 'Programa tus vídeos de TikTok',
    headlineAccent: 'y olvídate del móvil.',
    subhead:
      'Sube tus vídeos verticales desde el ordenador, escribe la descripción con IA y programa la publicación a la hora exacta. Sin notificaciones, sin "subir manualmente cuando te recuerda el móvil".',
    bullets: [
      'Vídeo vertical 9:16',
      'IA que escribe la descripción',
      'Multi-cuenta',
      '3 días gratis',
    ],
  },
  why: {
    eyebrow: '¿Por qué Zettapost para TikTok?',
    title: 'Programar TikTok desde el ordenador, en serio.',
    subtitle:
      'Pensado para creadores, marcas y agencias que producen vídeo en el ordenador y quieren publicarlo sin pasarlo al móvil.',
    items: [
      {
        Icon: Calendar,
        title: 'Programa cuándo quieras',
        description:
          'Elige hora exacta o usa la cola semanal. Zettapost publica solo, también a las 3 de la mañana si tu audiencia está activa entonces.',
      },
      {
        Icon: Film,
        title: 'Vídeo vertical sin trampas',
        description:
          'Subes el MP4 directamente desde el ordenador y se publica como vídeo nativo, sin marca de agua de otra app, sin compresión rara.',
      },
      {
        Icon: Sparkles,
        title: 'IA que escribe la descripción',
        description:
          'Sube el vídeo y la IA te genera la descripción con hashtags que encajan con tu nicho. Tú revisas, ajustas y programas.',
      },
      {
        Icon: BarChart3,
        title: 'Métricas reales',
        description:
          'Vistas, likes, comentarios y shares de cada vídeo, sincronizados con TikTok. Sabe qué horarios y qué formatos te están funcionando mejor.',
      },
    ],
  },
  guide: LANDING_GUIDES.tiktok,
  faqs: [
    {
      q: '¿Necesito cuenta de TikTok Business o Creator?',
      a: 'Recomendamos cuenta Business o Creator (ambas son gratis). La API oficial de TikTok solo permite programar publicaciones desde esos modos. Cambiar es gratis y se hace desde los ajustes de TikTok en menos de 30 segundos.',
    },
    {
      q: '¿Mi vídeo lleva marca de agua de Zettapost?',
      a: 'No. Subes el archivo original (sin marca de agua de TikTok ni de Zettapost) y se publica como vídeo nativo igual que si lo subieras desde la app.',
    },
    {
      q: '¿Cuánto puede durar el vídeo?',
      a: 'Soportamos los formatos que admite TikTok: vídeos cortos (hasta 60 segundos) y vídeos largos (hasta 10 minutos). El editor te avisa si el archivo se sale del límite.',
    },
    {
      q: '¿Puedo añadir música o sonido?',
      a: 'Subes el vídeo con el audio ya integrado. La música registrada de TikTok (la que sale del catálogo de la app) se añade desde TikTok directamente — esto es un límite de la API, no nuestro.',
    },
    {
      q: '¿Puedo conectar varias cuentas de TikTok?',
      a: 'Sí. Los planes con varias cuentas permiten gestionar TikTok de varios clientes o marcas desde un mismo panel, con sus colores y tono propios.',
    },
    {
      q: '¿Programáis también TikTok Stories?',
      a: 'De momento la API oficial de TikTok no permite programar Stories. En cuanto TikTok abra esa capacidad, lo añadiremos automáticamente.',
    },
  ],
  finalCta: {
    title: 'Programa TikTok',
    titleAccent: 'desde el portátil.',
    description: 'Crea tu cuenta y conecta TikTok gratis durante 3 días.',
  },
  related: [
    { to: '/programar-instagram', label: 'Programar Instagram' },
    { to: '/programar-youtube', label: 'Programar YouTube' },
    { to: '/precios', label: 'Ver precios' },
    { to: '/blog', label: 'Blog' },
  ],
};

export function ProgramarTiktok() {
  return <LandingTemplate data={data} />;
}
